import React, {
    useEffect,
    useState,
} from "react";

import {
    Box,
    Flex,
    Text,
    Divider,
    Icon,
    Input,
    Textarea,
} from "@chakra-ui/react";

import {
    Phone,
    MessageCircle,
    Mail,
    UserRound,
    Clock,
    CheckCircle,
    ArrowRight,
} from "lucide-react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ReusableAvatar from "components/Avatar/ReusableAvatar";

import StatusBadge, {
    getStatusVariant,
} from "../../../../components/Badges/StatusBadge";

import GradientButton from "components/Buttons/GradientButton";

import {
    updateContactLogAction,
} from "../../../../state/actions/contactLogActions";

import { useDispatch } from "react-redux";

import { formatDate } from "utils/formatDate";


const getMethodIcon = (method) => {
    switch (method) {
        case "CALL":
        case "Call":
            return Phone;

        case "WHATSAPP":
        case "WhatsApp":
            return MessageCircle;

        case "EMAIL":
        case "Email":
            return Mail;

        case "IN_PERSON":
        case "In Person":
            return UserRound;

        default:
            return UserRound;
    }
};


const getMethodLabel = (method) => {
    switch (method) {
        case "CALL":
        case "Call":
            return "Call";

        case "WHATSAPP":
        case "WhatsApp":
            return "WhatsApp";

        case "EMAIL":
        case "Email":
            return "Email";

        case "IN_PERSON":
        case "In Person":
            return "In Person";

        default:
            return method || "Unknown";
    }
};


const getStatusLabel = (status) => {
    switch (status) {
        case "CONTACTED":
            return "Contacted";

        case "NOT_REACHED":
            return "Not Reached";

        case "PENDING":
            return "Pending";

        case "Contacted":
            return "Contacted";

        case "Not Reached":
            return "Not Reached";

        case "Pending":
            return "Pending";

        default:
            return status || "Unknown";
    }
};


const ViewContactLogsDrawer = ({
    isOpen,
    onClose,
    logs = [],
    user = null,
    loading = false,
}) => {

    const dispatch = useDispatch();

    const [editingId, setEditingId] =
        useState(null);

    const [saving, setSaving] =
        useState(false);

    const [editForm, setEditForm] =
        useState({
            status: "",
            contact_method: "",
            reason: "",
            outcome: "",
            notes: "",
            next_action: "",
            follow_up_date: "",
        });


    const userName = user
        ? `${user.first_name || ""} ${
              user.last_name || ""
          }`.trim() || "User"
        : "User";


    useEffect(() => {
        if (!isOpen) {
            setEditingId(null);

            setEditForm({
                status: "",
                contact_method: "",
                reason: "",
                outcome: "",
                notes: "",
                next_action: "",
                follow_up_date: "",
            });
        }
    }, [isOpen]);


    const handleEdit = (log) => {

        setEditingId(
            log.contact_log_id
        );

        setEditForm({
            status:
                log.status || "Pending",

            contact_method:
                log.contact_method || "",

            reason:
                log.reason || "",

            outcome:
                log.outcome || "",

            notes:
                log.notes || "",

            next_action:
                log.next_action || "",

            follow_up_date:
                log.follow_up_date
                    ? String(
                          log.follow_up_date
                      ).substring(0, 10)
                    : "",
        });
    };


    const handleCancel = () => {

        setEditingId(null);

        setEditForm({
            status: "",
            contact_method: "",
            reason: "",
            outcome: "",
            notes: "",
            next_action: "",
            follow_up_date: "",
        });
    };


    const handleChange = (
        field,
        value
    ) => {

        setEditForm(
            (previous) => ({
                ...previous,
                [field]: value,
            })
        );
    };


    const handleSave = async (
        contactLogId
    ) => {

        try {

            setSaving(true);

            const result =
                await dispatch(
                    updateContactLogAction(
                        contactLogId,
                        editForm
                    )
                );

            if (result?.success) {
                setEditingId(null);
            }

        } finally {

            setSaving(false);

        }
    };


    return (
        <ReusableDrawer
            isOpen={isOpen}
            onClose={onClose}

            headerContent={
                <Text
                    fontSize="lg"
                    fontWeight="bold"
                >
                    Contact History
                </Text>
            }

            bodyContent={

                <Box p={5}>

                    {/* USER */}

                    <Flex
                        align="center"
                        gap={3}
                        mb={5}
                    >

                        <ReusableAvatar
                            name={userName}
                        />

                        <Box>

                            <Text
                                fontSize="lg"
                                fontWeight="700"
                                color="#071D54"
                            >
                                {userName}
                            </Text>

                            {user?.phone_number && (
                                <Text
                                    fontSize="sm"
                                    color="gray.500"
                                >
                                    {
                                        user.phone_number
                                    }
                                </Text>
                            )}

                        </Box>

                    </Flex>


                    <Divider mb={5} />


                    {/* HEADER */}

                    <Flex
                        justify="space-between"
                        align="center"
                        mb={4}
                    >

                        <Text
                            fontSize="md"
                            fontWeight="700"
                            color="#071D54"
                        >
                            Contact Logs
                        </Text>

                        <Text
                            fontSize="sm"
                            color="gray.500"
                        >
                            {logs.length}{" "}
                            {logs.length === 1
                                ? "log"
                                : "logs"}
                        </Text>

                    </Flex>


                    {/* LOADING */}

                    {loading ? (

                        <Text
                            textAlign="center"
                            color="gray.500"
                            py={10}
                        >
                            Loading contact history...
                        </Text>

                    ) : !logs.length ? (

                        <Box
                            textAlign="center"
                            py={10}
                        >

                            <Text
                                fontWeight="600"
                                color="#071D54"
                            >
                                No contact history
                            </Text>

                            <Text
                                fontSize="sm"
                                color="gray.500"
                                mt={1}
                            >
                                No contact logs have
                                been recorded for
                                this user.
                            </Text>

                        </Box>

                    ) : (

                        <Flex
                            direction="column"
                            gap={3}
                        >

                            {logs.map(
                                (
                                    log,
                                    index
                                ) => {

                                    const MethodIcon =
                                        getMethodIcon(
                                            log.contact_method
                                        );

                                    const isEditing =
                                        editingId ===
                                        log.contact_log_id;

                                    return (

                                        <Box
                                            key={
                                                log.contact_log_id ||
                                                index
                                            }
                                            border="1px solid"
                                            borderColor="gray.200"
                                            borderRadius="10px"
                                            p={4}
                                            bg="white"
                                        >

                                            {/* METHOD / DATE */}

                                            <Flex
                                                justify="space-between"
                                                align="flex-start"
                                                mb={4}
                                                gap={3}
                                            >

                                                <Flex
                                                    align="center"
                                                    gap={2}
                                                >

                                                    <Icon
                                                        as={
                                                            MethodIcon
                                                        }
                                                        boxSize={
                                                            17
                                                        }
                                                        color="gray.500"
                                                    />

                                                    {isEditing ? (

                                                        <select
                                                            value={
                                                                editForm.contact_method
                                                            }
                                                            onChange={(
                                                                e
                                                            ) =>
                                                                handleChange(
                                                                    "contact_method",
                                                                    e.target.value
                                                                )
                                                            }
                                                            style={{
                                                                padding:
                                                                    "7px 10px",
                                                                borderRadius:
                                                                    "7px",
                                                                border:
                                                                    "1px solid #E2E8F0",
                                                                background:
                                                                    "white",
                                                            }}
                                                        >

                                                            <option value="">
                                                                Select Method
                                                            </option>

                                                            <option value="Call">
                                                                Call
                                                            </option>

                                                            <option value="WhatsApp">
                                                                WhatsApp
                                                            </option>

                                                            <option value="Email">
                                                                Email
                                                            </option>

                                                            <option value="In Person">
                                                                In Person
                                                            </option>

                                                        </select>

                                                    ) : (

                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="700"
                                                            color="#071D54"
                                                        >
                                                            {
                                                                getMethodLabel(
                                                                    log.contact_method
                                                                )
                                                            }
                                                        </Text>

                                                    )}

                                                </Flex>


                                                <Text
                                                    fontSize="xs"
                                                    color="gray.500"
                                                >
                                                    {log.contacted_at
                                                        ? formatDate(
                                                              log.contacted_at
                                                          )
                                                        : "N/A"}
                                                </Text>

                                            </Flex>


                                            {/* STATUS */}

                                            <Box mb={4}>

                                                <Text
                                                    fontSize="xs"
                                                    color="gray.400"
                                                    mb={1}
                                                >
                                                    Status
                                                </Text>

                                                {isEditing ? (

                                                    <select
                                                        value={
                                                            editForm.status
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            handleChange(
                                                                "status",
                                                                e.target.value
                                                            )
                                                        }
                                                        style={{
                                                            width:
                                                                "100%",
                                                            padding:
                                                                "9px",
                                                            borderRadius:
                                                                "7px",
                                                            border:
                                                                "1px solid #E2E8F0",
                                                            background:
                                                                "white",
                                                        }}
                                                    >

                                                        <option value="Pending">
                                                            Pending
                                                        </option>

                                                        <option value="Contacted">
                                                            Contacted
                                                        </option>

                                                        <option value="Not Reached">
                                                            Not Reached
                                                        </option>

                                                    </select>

                                                ) : (

                                                    <StatusBadge
                                                        label={
                                                            getStatusLabel(
                                                                log.status
                                                            )
                                                        }
                                                        variant={
                                                            getStatusVariant(
                                                                log.status
                                                            )
                                                        }
                                                    />

                                                )}

                                            </Box>


                                            {/* REASON */}

                                            <Box mb={4}>

                                                <Text
                                                    fontSize="xs"
                                                    color="gray.400"
                                                    mb={1}
                                                >
                                                    Reason
                                                </Text>

                                                {isEditing ? (

                                                    <Input
                                                        size="sm"
                                                        value={
                                                            editForm.reason
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            handleChange(
                                                                "reason",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Enter reason"
                                                    />

                                                ) : (

                                                    <Text
                                                        fontSize="sm"
                                                        fontWeight="500"
                                                        color="#071D54"
                                                    >
                                                        {
                                                            log.reason ||
                                                            "No reason provided"
                                                        }
                                                    </Text>

                                                )}

                                            </Box>


                                            {/* NOTES */}

                                            <Box mb={4}>

                                                <Text
                                                    fontSize="xs"
                                                    color="gray.400"
                                                    mb={1}
                                                >
                                                    Notes
                                                </Text>

                                                {isEditing ? (

                                                    <Textarea
                                                        size="sm"
                                                        value={
                                                            editForm.notes
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            handleChange(
                                                                "notes",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Enter notes"
                                                    />

                                                ) : (

                                                    <Text
                                                        fontSize="sm"
                                                        color="gray.600"
                                                    >
                                                        {
                                                            log.notes ||
                                                            "No notes provided"
                                                        }
                                                    </Text>

                                                )}

                                            </Box>


                                            <Divider mb={4} />


                                            {/* OUTCOME */}

                                            <Box mb={4}>

                                                <Text
                                                    fontSize="xs"
                                                    color="gray.400"
                                                    mb={1}
                                                >
                                                    Outcome
                                                </Text>

                                                {isEditing ? (

                                                    <Input
                                                        size="sm"
                                                        value={
                                                            editForm.outcome
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            handleChange(
                                                                "outcome",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Enter outcome"
                                                    />

                                                ) : (

                                                    <Flex
                                                        align="center"
                                                        gap={2}
                                                    >

                                                        <Icon
                                                            as={
                                                                CheckCircle
                                                            }
                                                            boxSize={
                                                                15
                                                            }
                                                            color="gray.500"
                                                        />

                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="500"
                                                        >
                                                            {
                                                                log.outcome ||
                                                                "Pending"
                                                            }
                                                        </Text>

                                                    </Flex>

                                                )}

                                            </Box>


                                            {/* NEXT ACTION */}

                                            <Box mb={4}>

                                                <Text
                                                    fontSize="xs"
                                                    color="gray.400"
                                                    mb={1}
                                                >
                                                    Next Action
                                                </Text>

                                                {isEditing ? (

                                                    <Input
                                                        size="sm"
                                                        value={
                                                            editForm.next_action
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            handleChange(
                                                                "next_action",
                                                                e.target.value
                                                            )
                                                        }
                                                        placeholder="Enter next action"
                                                    />

                                                ) : (

                                                    <Flex
                                                        align="center"
                                                        gap={2}
                                                    >

                                                        <Icon
                                                            as={
                                                                ArrowRight
                                                            }
                                                            boxSize={
                                                                15
                                                            }
                                                            color="gray.500"
                                                        />

                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="500"
                                                        >
                                                            {
                                                                log.next_action ||
                                                                "No action set"
                                                            }
                                                        </Text>

                                                    </Flex>

                                                )}

                                            </Box>


                                            {/* FOLLOW UP */}

                                            <Box mb={4}>

                                                <Text
                                                    fontSize="xs"
                                                    color="gray.400"
                                                    mb={1}
                                                >
                                                    Follow Up
                                                </Text>

                                                {isEditing ? (

                                                    <Input
                                                        size="sm"
                                                        type="date"
                                                        value={
                                                            editForm.follow_up_date
                                                        }
                                                        onChange={(
                                                            e
                                                        ) =>
                                                            handleChange(
                                                                "follow_up_date",
                                                                e.target.value
                                                            )
                                                        }
                                                    />

                                                ) : (

                                                    <Flex
                                                        align="center"
                                                        gap={2}
                                                    >

                                                        <Icon
                                                            as={
                                                                Clock
                                                            }
                                                            boxSize={
                                                                15
                                                            }
                                                            color="gray.500"
                                                        />

                                                        <Text
                                                            fontSize="sm"
                                                            fontWeight="500"
                                                        >
                                                            {
                                                                log.follow_up_date ||
                                                                "N/A"
                                                            }
                                                        </Text>

                                                    </Flex>

                                                )}

                                            </Box>


                                            {/* ACTIONS */}

                                            <Flex
                                                justify="flex-end"
                                                gap={2}
                                                mt={4}
                                            >

                                                {isEditing ? (

                                                    <>

                                                        <GradientButton
                                                            onClick={
                                                                handleCancel
                                                            }
                                                            height="32px"
                                                        >
                                                            Cancel
                                                        </GradientButton>

                                                        <GradientButton
                                                            onClick={() =>
                                                                handleSave(
                                                                    log.contact_log_id
                                                                )
                                                            }
                                                            loading={
                                                                saving
                                                            }
                                                            height="32px"
                                                        >
                                                            Save
                                                        </GradientButton>

                                                    </>

                                                ) : (

                                                    <GradientButton
                                                        onClick={() =>
                                                            handleEdit(
                                                                log
                                                            )
                                                        }
                                                        height="30px"
                                                    >
                                                        Edit
                                                    </GradientButton>

                                                )}

                                            </Flex>

                                        </Box>
                                    );
                                }
                            )}

                        </Flex>
                    )}

                </Box>
            }
        />
    );
};

export default ViewContactLogsDrawer;